// Single source of truth for the input-guide text.
//   COMMAND_GUIDE — the "/" slash-commands (feeds the "/" autocomplete + the /help card).
//   TRIGGER_GUIDE — the three input triggers (/ commands, @ mention, # tag), shown on Welcome.
// A 3rd tuple element `true` marks a command as STAFF-ONLY — hidden from clients everywhere
// (the "/" autocomplete, the /help card) and server-gated. Filter with visibleCommands(isStaff).
export const COMMAND_GUIDE = [
  ["/create", "Create a bug, feature or story — opens a chooser"],
  ["/report", "This product's delivery report — tap to open the full report (private to you)"],
  ["/tickets", "View this project's tickets (last 30 days)"],
  ["/signoffdoc", "Show this product's latest sign-off document"],
  ["/test-apps", "Show the latest test build for this product — install links & what to test (private to you)"],
  ["/poll", "Create a poll — it's pinned to the channel until closed"],
  ["/idea", "Float an idea — everyone in the channel can back it and add to the discussion"],
  ["/campaign", "Build a marketing campaign brief — guided intake → brief → send to TMC (TMC only)", true],
  ["/summarize", "Summarise this channel's chat — last day, week or month (private to you)"],
  ["/ask", "Ask what was said about something in this channel — /ask <your question> (private to you)"],
  ["/nudge", "Nudge someone — shakes their screen 👋 · /nudge <name>"],
  ["/jump", "Jumpscare someone 😱 · /jump <name>"],
  ["/tutorial", "Replay the app tour"],
  ["/help", "Show the list of available commands"],
];

// The "Dream Team" ops channel has a DIFFERENT, cross-product command set (the product-scoped
// /bug, /feature, /report, /signoffdoc make no sense with no product). This channel is staff-only,
// so every command here is TMC-only by definition.
export const DREAMTEAM_GUIDE = [
  ["/portfolio", "Cross-product health board — every product at a glance"],
  ["/status", "One product's live snapshot — /status <product>"],
  ["/mywork", "Your assigned tickets across all products"],
  ["/aispend", "AI spend across products — /aispend [week|month]"],
  ["/broadcast", "Announce to EVERY product channel at once — /broadcast <message>"],
  ["/newproduct", "Create a new product — spins up a channel for all of TMC"],
  ["/poll", "Create a poll — it's pinned to the channel until closed"],
  ["/idea", "Float an idea — everyone in the channel can back it and add to the discussion"],
  ["/summarize", "Summarise this channel's chat — last day, week or month (private to you)", true],
  ["/ask", "Ask what was said about something in this channel — /ask <your question> (private to you)"],
  ["/nudge", "Nudge someone — shakes their screen 👋 · /nudge <name>"],
  ["/jump", "Jumpscare someone 😱 · /jump <name>"],
  ["/tutorial", "Replay the app tour"],
  ["/help", "Show the list of available commands"],
];

// The "Reporting" channel: Adscope marketing reports posted as PUBLIC cards. Access to the channel
// is the gate (staff implicitly, clients when added in Channel settings), so /adreport is open to
// every member — nothing here is staff-only.
export const REPORTING_GUIDE = [
  ["/adreport", "Affinity marketing report (last 7 days) — posted here for everyone · /adreport mtd | lastmonth | 2026-08-01..2026-08-15"],
  ["/summarize", "Summarise this channel's chat — last day, week or month (private to you)"],
  ["/ask", "Ask what was said about something in this channel — /ask <your question> (private to you)"],
  ["/nudge", "Nudge someone — shakes their screen 👋 · /nudge <name>"],
  ["/jump", "Jumpscare someone 😱 · /jump <name>"],
  ["/tutorial", "Replay the app tour"],
  ["/help", "Show the list of available commands"],
];

// A staff-made channel with no TMCHub product behind it ("+ New channel"): the product-less set.
export const CUSTOM_GUIDE = [
  ["/poll", "Create a poll — it's pinned to the channel until closed"],
  ["/idea", "Float an idea — everyone in the channel can back it and add to the discussion"],
  ["/summarize", "Summarise this channel's chat — last day, week or month (private to you)"],
  ["/ask", "Ask what was said about something in this channel — /ask <your question> (private to you)"],
  ["/nudge", "Nudge someone — shakes their screen 👋 · /nudge <name>"],
  ["/jump", "Jumpscare someone 😱 · /jump <name>"],
  ["/tutorial", "Replay the app tour"],
  ["/help", "Show the list of available commands"],
];

// The commands visible to a given user in a channel of the given kind. Dream Team channels
// (kind === "dreamteam") use the cross-product set, the Reporting channel (kind === "reporting")
// its report set; all others use the product set. Clients never see staff-only entries (and never
// see the Dream Team channel at all).
const guideFor = (kind) => (kind === "dreamteam" ? DREAMTEAM_GUIDE : kind === "reporting" ? REPORTING_GUIDE : kind === "custom" ? CUSTOM_GUIDE : COMMAND_GUIDE);
export const visibleCommands = (isStaff, kind) =>
  guideFor(kind).filter(([, , staffOnly]) => isStaff || !staffOnly);

// The three ways to trigger something from the message box — shown on the Welcome screen.
export const TRIGGER_GUIDE = [
  ["/", "Run a command — report a bug, request a feature, pull a report, view tickets & more"],
  ["@", "Mention a teammate so they're notified"],
  ["#", "Tag a ticket in your message"],
];
